import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlineCalendarDays, HiOutlineClipboardDocumentList, HiOutlineShoppingBag,
  HiOutlineArchiveBox, HiOutlineBanknotes, HiOutlineUsers, HiOutlineCalculator,
  HiOutlineArrowRight, HiOutlineCheck, HiOutlineSparkles, HiOutlineChevronDown,
  HiOutlineExclamationTriangle, HiOutlineXMark,
} from 'react-icons/hi2';
import { detectInAppBrowser } from './InAppBrowserWarning';

// ─────────────────────────────────────────────────────────────
// Landing Page — Metria
// Design system: Warm minimalism + Fraunces (display) + Inter (UI)
// Colors: theme primary #C4786E, primaryLight #F5E6E3, accent #E8B4A0
// ─────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: HiOutlineShoppingBag,          titulo: 'Pedidos',            desc: 'Agenda visual por data, pagamentos parciais, avanço de status em 1 clique e ficha impressa direto do card.' },
  { icon: HiOutlineClipboardDocumentList, titulo: 'Fichas técnicas',    desc: 'Ingredientes, preparações base e embalagens. CMV automático e preço de venda com margem que você define.' },
  { icon: HiOutlineArchiveBox,           titulo: 'Estoque inteligente', desc: 'Ingredientes e embalagens com custo, mínimo e alerta. Previsão de compras baseada no seu consumo real.' },
  { icon: HiOutlineBanknotes,            titulo: 'Finanças',            desc: 'Receita, despesa e lucro do mês. Extrato em PDF, saldo por método de pagamento e caixa inicial.' },
  { icon: HiOutlineUsers,                titulo: 'Clientes',            desc: 'Cadastro com telefone e histórico. WhatsApp em 1 toque, aniversariantes do mês e recorrência.' },
  { icon: HiOutlineCalculator,           titulo: 'Calculadora',         desc: 'Escale qualquer receita por gramas, kg, porções ou N receitas. Imprime lista em papel térmico 80mm.' },
];

const PASSOS = [
  { n: 1, titulo: 'Cadastre seus ingredientes',   desc: 'Insumos e embalagens com custo, mínimo e unidade.' },
  { n: 2, titulo: 'Monte suas receitas base',      desc: 'Massas, recheios, caldas e coberturas com CMV.' },
  { n: 3, titulo: 'Configure recheios e tamanhos', desc: 'Combinações de bolos com preço automático.' },
  { n: 4, titulo: 'Cadastre clientes e pedidos',   desc: 'A produção do dia é gerada automaticamente.' },
];

const INCLUDES = [
  'Pedidos, produtos e clientes ilimitados',
  'Fichas técnicas com CMV automático',
  'Guia de produção diário e semanal',
  'Módulo financeiro completo',
  'Sem taxa por venda',
  'Suporte por e-mail',
];

const FAQ = [
  {
    q: 'Preciso instalar alguma coisa?',
    a: 'Não. O Metria roda no navegador do celular, tablet ou computador. Sem downloads, sem app, sem instalação.',
  },
  {
    q: 'E se eu quiser cancelar?',
    a: 'Você cancela a qualquer momento direto no painel de configurações. Sem fidelidade, sem multa, sem ligar para ninguém.',
  },
  {
    q: 'Meus dados ficam seguros?',
    a: 'Cada confeitaria tem um banco de dados isolado — seus pedidos, receitas e clientes nunca se misturam com os de outra. Backup automático diário.',
  },
  {
    q: 'Funciona no celular?',
    a: 'Sim. O Metria é responsivo — todas as telas foram pensadas para tocar de balcão, cozinha ou entrega.',
  },
];

export default function Landing() {
  useReveal();
  const [inAppBrowser, setInAppBrowser] = useState(null);
  const [inAppDismissed, setInAppDismissed] = useState(false);
  useEffect(() => { setInAppBrowser(detectInAppBrowser()); }, []);

  return (
    <div className="lp-root">
      <LandingStyles />

      <a href="#main" className="lp-skip">Pular para o conteúdo</a>

      {inAppBrowser && !inAppDismissed && (
        <div className="lp-inapp-banner" role="alert">
          <HiOutlineExclamationTriangle size={18} aria-hidden="true" />
          <span>
            Você está no navegador do <strong>{inAppBrowser}</strong>. Para criar
            sua conta, abra este link no <strong>Chrome</strong> ou <strong>Safari</strong>.
          </span>
          <button
            type="button"
            className="lp-inapp-close"
            aria-label="Fechar aviso"
            onClick={() => setInAppDismissed(true)}
          >
            <HiOutlineXMark size={16} />
          </button>
        </div>
      )}

      {/* ─── Nav ─────────────────────────────── */}
      <header className="lp-nav" role="banner">
        <div className="lp-container lp-nav-inner">
          <Link to="/" className="lp-nav-logo" aria-label="Metria — início">
            <img src="/metria-logo.svg" alt="Metria" />
          </Link>
          <nav className="lp-nav-actions" aria-label="Ações principais">
            <Link to="/login" className="lp-btn lp-btn-ghost">Já tenho conta</Link>
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-sm">
              Começar grátis
            </Link>
          </nav>
        </div>
      </header>

      <main id="main">

        {/* ─── Hero ────────────────────────────── */}
        <section className="lp-hero" aria-labelledby="hero-title">
          <div className="lp-hero-bg" aria-hidden="true">
            <div className="lp-blob lp-blob-a" />
            <div className="lp-blob lp-blob-b" />
            <div className="lp-blob lp-blob-c" />
          </div>

          <div className="lp-container lp-hero-inner">
            <div className="lp-hero-copy" data-reveal>
              <img src="/metria-logo.svg" alt="Metria" className="lp-hero-logo" />
              <span className="lp-eyebrow">
                <HiOutlineSparkles /> Assistente para Confeiteiras
              </span>
              <h1 id="hero-title" className="lp-h1">
                Do pedido à produção,{' '}
                <span className="lp-italic">sem planilha.</span>
              </h1>
              <p className="lp-sub">
                Pedidos, produção, precificação, financeiro e controle de estoque:{' '}
                <strong>tudo o que sua confeitaria precisa pra crescer.</strong>
              </p>
              <div className="lp-cta-row">
                <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">
                  Começar 30 dias grátis
                  <HiOutlineArrowRight aria-hidden="true" />
                </Link>
                <Link to="/login" className="lp-btn lp-btn-outline lp-btn-lg">
                  Já tenho conta
                </Link>
              </div>
              <ul className="lp-trust-chips" role="list" aria-label="Detalhes do teste">
                <li><HiOutlineCheck aria-hidden="true" /> Sem cartão</li>
                <li><HiOutlineCheck aria-hidden="true" /> <strong>R$&nbsp;39,90/mês</strong> depois</li>
                <li><HiOutlineCheck aria-hidden="true" /> Cancele quando quiser</li>
              </ul>
            </div>

            <div className="lp-hero-mock" data-reveal aria-hidden="true">
              <MockupPedidos />
            </div>
          </div>
        </section>

        {/* ─── O trunfo: Guia de Produção ─────── */}
        <section className="lp-showcase" aria-labelledby="showcase-title">
          <div className="lp-container">
            <div className="lp-showcase-grid">
              <div className="lp-showcase-copy" data-reveal>
                <span className="lp-eyebrow">
                  <HiOutlineSparkles /> Sem planilha, sem esquecer nada
                </span>
                <h2 id="showcase-title" className="lp-h2">
                  Nunca mais esqueça de{' '}
                  <span className="lp-italic">produzir um bolo.</span>
                </h2>
                <p className="lp-showcase-text">
                  A partir dos pedidos da semana, o Metria consolida <strong>quanto de cada
                  ingrediente</strong> você precisa e <strong>quantas receitas</strong> preparar de
                  cada massa e recheio.
                </p>
                <ul className="lp-showcase-list" role="list">
                  <li><HiOutlineCheck aria-hidden="true" /> Montagem diária a partir dos pedidos</li>
                  <li><HiOutlineCheck aria-hidden="true" /> Produção semanal consolidada</li>
                  <li><HiOutlineCheck aria-hidden="true" /> Fichas individuais impressas (opcional)</li>
                  <li><HiOutlineCheck aria-hidden="true" /> Sobras calculadas para reduzir desperdício</li>
                </ul>
                <Link to="/register" className="lp-btn lp-btn-primary">
                  Testar o guia grátis
                  <HiOutlineArrowRight aria-hidden="true" />
                </Link>
              </div>

              <div className="lp-showcase-mock" data-reveal>
                <MockupGuia />
              </div>
            </div>
          </div>
        </section>

        {/* ─── Bento de features ───────────────── */}
        <section className="lp-features" aria-labelledby="features-title">
          <div className="lp-container">
            <div className="lp-section-head" data-reveal>
              <span className="lp-eyebrow">Tudo em um só lugar</span>
              <h2 id="features-title" className="lp-h2">
                Feito para o dia a dia da{' '}
                <span className="lp-italic">confeitaria de encomenda.</span>
              </h2>
              <p className="lp-lead">
                Cada módulo pensado a partir do fluxo real de quem produz sob demanda —
                não é adaptação de sistema genérico.
              </p>
            </div>

            <div className="lp-bento">
              {FEATURES.map((f, i) => (
                <article key={i} className="lp-card lp-feature" data-reveal>
                  <div className="lp-feature-icon">
                    <f.icon aria-hidden="true" />
                  </div>
                  <h3 className="lp-h4">{f.titulo}</h3>
                  <p className="lp-body-sm">{f.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Como começa ─────────────────────── */}
        <section className="lp-steps" aria-labelledby="steps-title">
          <div className="lp-container">
            <div className="lp-section-head" data-reveal>
              <span className="lp-eyebrow lp-eyebrow-dark">Como começa</span>
              <h2 id="steps-title" className="lp-h2 lp-h2-white">
                Em minutos, sua confeitaria{' '}
                <span className="lp-italic">está rodando.</span>
              </h2>
            </div>
            <ol className="lp-steps-list" role="list">
              {PASSOS.map((p) => (
                <li key={p.n} className="lp-step" data-reveal>
                  <div className="lp-step-num" aria-hidden="true">
                    {String(p.n).padStart(2, '0')}
                  </div>
                  <h3 className="lp-step-title">{p.titulo}</h3>
                  <p className="lp-step-desc">{p.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ─── Preço ───────────────────────────── */}
        <section className="lp-pricing" aria-labelledby="pricing-title">
          <div className="lp-container">
            <div className="lp-section-head" data-reveal>
              <span className="lp-eyebrow">Plano único · sem pegadinhas</span>
              <h2 id="pricing-title" className="lp-h2">
                Tudo incluído por um{' '}
                <span className="lp-italic">preço só.</span>
              </h2>
            </div>

            <div className="lp-price-card" data-reveal>
              <div className="lp-price-ribbon">
                30 dias grátis · sem cartão
              </div>
              <div className="lp-price-plan">Completo Mensal</div>
              <div className="lp-price-value">
                <span className="lp-price-curr">R$</span>
                <span className="lp-price-num">39</span>
                <span className="lp-price-cents">,90</span>
                <span className="lp-price-period">/mês</span>
              </div>
              <p className="lp-price-fine">
                Após 30 dias grátis. Cancele quando quiser.
              </p>
              <ul className="lp-price-list" role="list">
                {INCLUDES.map((item, i) => (
                  <li key={i}>
                    <HiOutlineCheck aria-hidden="true" /> {item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg lp-btn-block">
                Começar 30 dias grátis
                <HiOutlineArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* ─── FAQ ─────────────────────────────── */}
        <section className="lp-faq" aria-labelledby="faq-title">
          <div className="lp-container lp-faq-inner">
            <div className="lp-section-head" data-reveal>
              <span className="lp-eyebrow">Perguntas frequentes</span>
              <h2 id="faq-title" className="lp-h2">
                Ainda com{' '}
                <span className="lp-italic">dúvida?</span>
              </h2>
            </div>
            <div className="lp-faq-list">
              {FAQ.map((f, i) => (
                <details key={i} className="lp-faq-item" data-reveal>
                  <summary>
                    <span>{f.q}</span>
                    <HiOutlineChevronDown aria-hidden="true" />
                  </summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA final ───────────────────────── */}
        <section className="lp-final" aria-labelledby="final-title">
          <div className="lp-container lp-final-inner" data-reveal>
            <h2 id="final-title" className="lp-h2 lp-h2-white">
              Pronta para profissionalizar sua{' '}
              <span className="lp-italic">confeitaria?</span>
            </h2>
            <p className="lp-final-sub">
              Comece com 30 dias grátis. Sem cartão, sem compromisso.
            </p>
            <div className="lp-cta-row lp-cta-row-center">
              <Link to="/register" className="lp-btn lp-btn-white lp-btn-lg">
                Criar minha confeitaria
                <HiOutlineArrowRight aria-hidden="true" />
              </Link>
              <Link to="/login" className="lp-btn lp-btn-outline-white lp-btn-lg">
                Já tenho conta
              </Link>
            </div>
          </div>
        </section>
      </main>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Mockups em CSS
// ─────────────────────────────────────────────────────────────

function MockupPedidos() {
  // Calendário mock: julho 2026. Domingo(0) → Sábado(6). Julho 2026 começa numa quarta (getDay = 3).
  const startWeekday = 3; // Qua
  const daysInMonth = 31;
  const today = 17;
  // Mapa de dia → { count, tone }. tone: 'primary' (padrão), 'accent' (destaque)
  const events = {
    3:  { count: 1 },
    8:  { count: 2 },
    11: { count: 1 },
    15: { count: 1 },
    17: { count: 3, hot: true },
    22: { count: 2 },
    24: { count: 1 },
    28: { count: 2 },
  };
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ empty: true, k: `e-${i}` });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, ev: events[d], isToday: d === today, k: `d-${d}` });
  // Preenche até completar múltiplo de 7
  while (cells.length % 7 !== 0) cells.push({ empty: true, k: `t-${cells.length}` });

  return (
    <div className="lp-mock lp-mock-window">
      <div className="lp-mock-chrome">
        <span className="lp-mock-dot lp-mock-dot-r" />
        <span className="lp-mock-dot lp-mock-dot-y" />
        <span className="lp-mock-dot lp-mock-dot-g" />
        <div className="lp-mock-url">metria.demiplie.com.br/pedidos</div>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-title-row">
          <div className="lp-mock-title">Pedidos</div>
          <div className="lp-mock-cta">+ Novo Pedido</div>
        </div>
        <div className="lp-mock-date">Julho · <strong>14 vendas</strong> · R$ 1.680,00</div>

        <div className="lp-cal">
          <div className="lp-cal-head">
            <span className="lp-cal-nav" aria-hidden="true">‹</span>
            <span className="lp-cal-month">Julho 2026</span>
            <span className="lp-cal-nav" aria-hidden="true">›</span>
          </div>
          <div className="lp-cal-week">
            {['D','S','T','Q','Q','S','S'].map((w, i) => (
              <span key={i} className="lp-cal-wd">{w}</span>
            ))}
          </div>
          <div className="lp-cal-grid">
            {cells.map((c) => {
              if (c.empty) return <span key={c.k} className="lp-cal-cell lp-cal-empty" />;
              const cls = [
                'lp-cal-cell',
                c.isToday ? 'is-today' : '',
                c.ev ? 'has-ev' : '',
                c.ev && c.ev.hot ? 'is-hot' : '',
              ].filter(Boolean).join(' ');
              return (
                <span key={c.k} className={cls}>
                  <span className="lp-cal-num">{c.day}</span>
                  {c.ev && (
                    <span className="lp-cal-badge" aria-label={`${c.ev.count} pedidos`}>
                      {c.ev.count}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <div className="lp-cal-legend">
            <span className="lp-cal-lg-item"><span className="lp-cal-lg-dot lp-cal-lg-today" /> Hoje</span>
            <span className="lp-cal-lg-item"><span className="lp-cal-lg-dot lp-cal-lg-ev" /> Com pedidos</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockupGuia() {
  return (
    <div className="lp-mock lp-mock-guia">
      <div className="lp-mock-chrome">
        <span className="lp-mock-dot lp-mock-dot-r" />
        <span className="lp-mock-dot lp-mock-dot-y" />
        <span className="lp-mock-dot lp-mock-dot-g" />
        <div className="lp-mock-url">Guia · Montagem Diária</div>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-guia-head">
          <div>
            <div className="lp-mock-title">Produção de hoje</div>
            <div className="lp-mock-date">Quarta-feira · 8 bolos · 3 sabores</div>
          </div>
          <span className="lp-mock-badge">Auto</span>
        </div>

        <div className="lp-mock-block">
          <div className="lp-mock-block-title">Massas</div>
          <div className="lp-mock-row">
            <span>Baunilha</span>
            <span className="lp-mock-strong">3× receita · 2,4 kg</span>
          </div>
          <div className="lp-mock-row">
            <span>Chocolate</span>
            <span className="lp-mock-strong">2× receita · 1,6 kg</span>
          </div>
        </div>

        <div className="lp-mock-block">
          <div className="lp-mock-block-title">Recheios</div>
          <div className="lp-mock-row">
            <span>Brigadeiro</span>
            <span className="lp-mock-strong">2× · 1.000 g</span>
          </div>
          <div className="lp-mock-row">
            <span>Ninho c/ Nutella</span>
            <span className="lp-mock-strong">1× · 500 g</span>
          </div>
        </div>

        <div className="lp-mock-foot">
          <span className="lp-mock-check">✓</span>
          Sobra prevista: <strong>120 g</strong> (aproveitamento 96%)
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Reveal on scroll (respeita prefers-reduced-motion)
// ─────────────────────────────────────────────────────────────
function useReveal() {
  const observer = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Sem animação: marca tudo como visível
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('[data-reveal]').forEach((el) => observer.current.observe(el));
    return () => { if (observer.current) observer.current.disconnect(); };
  }, []);
}

// ─────────────────────────────────────────────────────────────
// Estilos (CSS-in-JS via <style> — self-contained)
// ─────────────────────────────────────────────────────────────
function LandingStyles() {
  return (
    <style>{`
      .lp-root {
        --lp-primary: #C4786E;
        --lp-primary-dark: #A35D54;
        --lp-primary-light: #F5E6E3;
        --lp-accent: #E8B4A0;
        --lp-gold: #C69963;
        --lp-ink: #1F1918;
        --lp-text: #2B2320;
        --lp-muted: #7A6E68;
        --lp-soft: #A99A93;
        --lp-bg: #FAF6F3;
        --lp-bg-elev: #FFFFFF;
        --lp-border: #EDE5E1;
        --lp-border-strong: #E1D5CE;
        --lp-radius-sm: 10px;
        --lp-radius-md: 16px;
        --lp-radius-lg: 22px;
        --lp-radius-xl: 28px;
        --lp-shadow-sm: 0 1px 2px rgba(31, 25, 24, 0.04), 0 2px 8px rgba(31, 25, 24, 0.04);
        --lp-shadow-md: 0 2px 4px rgba(31, 25, 24, 0.05), 0 12px 32px rgba(31, 25, 24, 0.08);
        --lp-shadow-lg: 0 4px 8px rgba(31, 25, 24, 0.06), 0 24px 60px rgba(31, 25, 24, 0.12);
        --lp-shadow-glow: 0 12px 36px rgba(196, 120, 110, 0.32);
        --lp-serif: 'Fraunces', ui-serif, Georgia, serif;
        --lp-sans: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
        font-family: var(--lp-sans);
        color: var(--lp-text);
        background: var(--lp-bg);
        min-height: 100vh;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .lp-root *, .lp-root *::before, .lp-root *::after { box-sizing: border-box; }

      /* Skip link (a11y) */
      .lp-skip {
        position: absolute; top: -40px; left: 12px;
        background: var(--lp-primary); color: #fff;
        padding: 8px 14px; border-radius: 8px;
        font-size: 14px; font-weight: 600; text-decoration: none;
        z-index: 200;
      }
      .lp-skip:focus { top: 12px; }

      .lp-container {
        max-width: 1160px;
        margin: 0 auto;
        padding: 0 20px;
      }
      @media (min-width: 768px) { .lp-container { padding: 0 32px; } }

      /* ── Typography ───────────────────────────────── */
      .lp-h1 {
        font-family: var(--lp-serif);
        font-weight: 700;
        font-size: clamp(38px, 5.5vw, 68px);
        line-height: 1.02;
        letter-spacing: -0.025em;
        color: var(--lp-ink);
        margin: 0 0 20px;
        text-wrap: balance;
      }
      .lp-h2 {
        font-family: var(--lp-serif);
        font-weight: 700;
        font-size: clamp(28px, 4vw, 44px);
        line-height: 1.1;
        letter-spacing: -0.02em;
        color: var(--lp-ink);
        margin: 12px 0 12px;
        text-wrap: balance;
      }
      .lp-h2-white { color: #fff; }
      .lp-h4 {
        font-family: var(--lp-sans);
        font-weight: 700;
        font-size: 17px;
        line-height: 1.3;
        letter-spacing: -0.01em;
        color: var(--lp-ink);
        margin: 0 0 8px;
      }
      .lp-italic {
        font-style: italic;
        font-weight: 500;
        color: var(--lp-primary-dark);
        font-family: var(--lp-serif);
      }
      .lp-sub {
        font-size: clamp(16px, 1.6vw, 19px);
        color: var(--lp-muted);
        line-height: 1.55;
        margin: 0 0 32px;
        max-width: 540px;
      }
      .lp-sub strong { color: var(--lp-text); font-weight: 600; }
      .lp-lead {
        font-size: 16px;
        color: var(--lp-muted);
        line-height: 1.6;
        max-width: 560px;
        margin: 0 auto;
      }
      .lp-body-sm { font-size: 14px; line-height: 1.55; color: var(--lp-muted); margin: 0; }

      .lp-eyebrow {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 12px; font-weight: 700;
        text-transform: uppercase; letter-spacing: 0.08em;
        color: var(--lp-primary-dark);
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid var(--lp-primary-light);
        padding: 6px 12px; border-radius: 999px;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .lp-eyebrow-dark {
        background: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.22);
        color: rgba(255, 255, 255, 0.95);
      }
      .lp-eyebrow svg { width: 14px; height: 14px; }

      /* ── Buttons ─────────────────────────────────── */
      .lp-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        font-family: var(--lp-sans);
        font-size: 15px; font-weight: 600; letter-spacing: -0.01em;
        padding: 12px 22px;
        border-radius: 12px;
        border: 1.5px solid transparent;
        text-decoration: none;
        cursor: pointer;
        transition: transform 180ms ease, box-shadow 220ms ease, background 180ms ease, color 180ms ease, border-color 180ms ease;
        white-space: nowrap;
        min-height: 44px;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
      /* Override global 'a:hover' do App.css que vinha mudando a cor do texto */
      .lp-root a.lp-btn-primary,
      .lp-root a.lp-btn-primary:hover,
      .lp-root a.lp-btn-primary:focus { color: #fff; }
      .lp-root a.lp-btn-outline,
      .lp-root a.lp-btn-outline:hover,
      .lp-root a.lp-btn-outline:focus { color: var(--lp-primary-dark); }
      .lp-root a.lp-btn-ghost,
      .lp-root a.lp-btn-ghost:hover,
      .lp-root a.lp-btn-ghost:focus { color: var(--lp-text); }
      .lp-root a.lp-btn-ghost:hover { color: var(--lp-primary-dark); }
      .lp-root a.lp-btn-white,
      .lp-root a.lp-btn-white:hover,
      .lp-root a.lp-btn-white:focus { color: var(--lp-primary-dark); }
      .lp-root a.lp-btn-outline-white,
      .lp-root a.lp-btn-outline-white:hover,
      .lp-root a.lp-btn-outline-white:focus { color: #fff; }
      .lp-btn:focus-visible {
        outline: 3px solid rgba(196, 120, 110, 0.4);
        outline-offset: 3px;
      }
      .lp-btn svg { width: 18px; height: 18px; flex-shrink: 0; }
      .lp-btn-sm { padding: 9px 16px; font-size: 14px; min-height: 40px; }
      .lp-btn-lg { padding: 14px 26px; font-size: 16px; min-height: 52px; }
      .lp-btn-block { display: flex; width: 100%; }

      .lp-btn-primary {
        background: var(--lp-primary);
        color: #fff;
        box-shadow: var(--lp-shadow-glow);
      }
      .lp-btn-primary:hover {
        background: var(--lp-primary-dark);
        transform: translateY(-1px);
        box-shadow: 0 16px 44px rgba(196, 120, 110, 0.42);
      }
      .lp-btn-outline {
        background: transparent;
        color: var(--lp-primary-dark);
        border-color: var(--lp-primary);
      }
      .lp-btn-outline:hover {
        background: var(--lp-primary-light);
        transform: translateY(-1px);
      }
      .lp-btn-ghost {
        background: transparent;
        color: var(--lp-text);
        border-color: transparent;
      }
      .lp-btn-ghost:hover { background: var(--lp-primary-light); color: var(--lp-primary-dark); }
      .lp-btn-white {
        background: #fff;
        color: var(--lp-primary-dark);
        box-shadow: var(--lp-shadow-md);
      }
      .lp-btn-white:hover { transform: translateY(-1px); box-shadow: var(--lp-shadow-lg); }
      .lp-btn-outline-white {
        background: transparent; color: #fff;
        border-color: rgba(255, 255, 255, 0.6);
      }
      .lp-btn-outline-white:hover {
        background: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.9);
      }

      /* ── Nav ─────────────────────────────────────── */
      .lp-nav {
        position: sticky; top: 0; z-index: 50;
        background: rgba(250, 246, 243, 0.75);
        backdrop-filter: saturate(180%) blur(14px);
        -webkit-backdrop-filter: saturate(180%) blur(14px);
        border-bottom: 1px solid rgba(237, 229, 225, 0.7);
      }
      .lp-nav-inner {
        display: flex; align-items: center; justify-content: space-between;
        gap: 16px;
        /* No app o webview vai ate a borda: sem a area segura o logo fica
           embaixo do relogio e da bateria. Na web o env() vale 0. */
        padding: calc(12px + env(safe-area-inset-top)) 20px 12px;
      }
      @media (min-width: 768px) {
        .lp-nav-inner { padding: calc(14px + env(safe-area-inset-top)) 32px 14px; }
      }
      .lp-nav-logo { display: block; text-decoration: none; }
      .lp-nav-logo img { height: 44px; width: auto; display: block; }
      .lp-nav-actions { display: flex; align-items: center; gap: 8px; }
      @media (max-width: 480px) {
        .lp-nav-logo img { height: 36px; }
        .lp-btn-sm { padding: 8px 12px; font-size: 13px; }
        .lp-nav-actions .lp-btn-ghost { display: none; }
      }

      /* ── Hero ────────────────────────────────────── */
      .lp-hero {
        position: relative;
        padding: 72px 0 100px;
        overflow: hidden;
        isolation: isolate;
      }
      @media (min-width: 1024px) { .lp-hero { padding: 100px 0 140px; } }

      .lp-hero-bg {
        position: absolute; inset: 0;
        z-index: -1;
        background:
          radial-gradient(ellipse at 20% 0%, rgba(232, 180, 160, 0.28), transparent 55%),
          radial-gradient(ellipse at 90% 40%, rgba(196, 120, 110, 0.14), transparent 55%),
          linear-gradient(180deg, #FDF7F4 0%, #FAF6F3 100%);
      }
      .lp-blob {
        position: absolute; border-radius: 50%;
        filter: blur(64px);
        opacity: 0.6;
        pointer-events: none;
      }
      .lp-blob-a { top: -80px; right: -60px; width: 340px; height: 340px; background: var(--lp-accent); }
      .lp-blob-b { bottom: -100px; left: -80px; width: 380px; height: 380px; background: var(--lp-primary-light); }
      .lp-blob-c { top: 30%; left: 40%; width: 220px; height: 220px; background: rgba(198, 153, 99, 0.28); }

      .lp-hero-logo {
        display: block;
        height: 96px;
        width: auto;
        margin: 0 0 20px;
        filter: drop-shadow(0 4px 12px rgba(196, 120, 110, 0.15));
      }
      @media (max-width: 767px) {
        .lp-hero-logo { height: 72px; margin-bottom: 16px; }
      }
      @media (max-width: 380px) {
        .lp-hero-logo { height: 60px; }
      }

      .lp-hero-inner {
        display: grid;
        grid-template-columns: 1fr;
        gap: 48px;
        align-items: center;
      }
      @media (min-width: 1024px) {
        .lp-hero-inner { grid-template-columns: 1.05fr 1fr; gap: 60px; }
      }
      .lp-cta-row {
        display: flex; gap: 12px; flex-wrap: wrap;
        margin-bottom: 24px;
      }
      .lp-cta-row-center { justify-content: center; }

      .lp-trust-chips {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-wrap: wrap; gap: 8px;
        justify-content: center;
      }
      @media (min-width: 1024px) {
        .lp-trust-chips { justify-content: flex-start; }
      }
      .lp-trust-chips li {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 6px 12px; border-radius: 999px;
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid var(--lp-border);
        color: var(--lp-muted);
        font-size: 13px; font-weight: 500;
        white-space: nowrap;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .lp-trust-chips li strong { color: var(--lp-text); font-weight: 600; }
      .lp-trust-chips li svg {
        width: 14px; height: 14px; flex-shrink: 0;
        color: var(--lp-primary);
        stroke-width: 3;
      }

      /* ── Section head compartilhado ─────────────── */
      .lp-section-head { text-align: center; max-width: 720px; margin: 0 auto 48px; }
      .lp-section-head .lp-h2 { margin-top: 14px; }
      .lp-section-head .lp-lead { margin-top: 12px; }
      @media (min-width: 768px) { .lp-section-head { margin-bottom: 64px; } }

      /* ── Showcase (Guia de Produção) ─────────────── */
      .lp-showcase {
        position: relative;
        background: linear-gradient(180deg, #FAF6F3 0%, #FFFFFF 100%);
        padding: 88px 0 100px;
        border-top: 1px solid var(--lp-border);
      }
      @media (min-width: 1024px) { .lp-showcase { padding: 120px 0 140px; } }
      .lp-showcase-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 48px;
        align-items: center;
      }
      @media (min-width: 1024px) {
        .lp-showcase-grid { grid-template-columns: 1fr 1.05fr; gap: 72px; }
      }
      .lp-showcase-text {
        font-size: 17px; line-height: 1.6;
        color: var(--lp-muted); margin: 0 0 24px;
      }
      .lp-showcase-text strong { color: var(--lp-text); font-weight: 600; }
      .lp-showcase-list {
        list-style: none; padding: 0; margin: 0 0 32px;
        display: flex; flex-direction: column; gap: 12px;
      }
      .lp-showcase-list li {
        display: flex; align-items: flex-start; gap: 10px;
        font-size: 15px; color: var(--lp-text);
        font-weight: 500;
      }
      .lp-showcase-list svg {
        flex-shrink: 0; width: 22px; height: 22px;
        padding: 3px;
        background: var(--lp-primary-light); color: var(--lp-primary-dark);
        border-radius: 999px;
        stroke-width: 2.5;
      }

      /* ── Features Bento ──────────────────────────── */
      .lp-features {
        padding: 88px 0;
        background: var(--lp-bg-elev);
        border-top: 1px solid var(--lp-border);
      }
      @media (min-width: 1024px) { .lp-features { padding: 120px 0; } }
      .lp-bento {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
      }
      @media (min-width: 640px) { .lp-bento { grid-template-columns: repeat(2, 1fr); gap: 18px; } }
      @media (min-width: 1024px) { .lp-bento { grid-template-columns: repeat(3, 1fr); gap: 20px; } }

      .lp-card {
        background: var(--lp-bg-elev);
        border: 1px solid var(--lp-border);
        border-radius: var(--lp-radius-lg);
        padding: 28px 26px;
        transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
      }
      .lp-card:hover {
        transform: translateY(-3px);
        box-shadow: var(--lp-shadow-md);
        border-color: var(--lp-border-strong);
      }
      .lp-feature-icon {
        width: 48px; height: 48px;
        border-radius: 14px;
        background: linear-gradient(135deg, var(--lp-primary-light), rgba(232, 180, 160, 0.4));
        color: var(--lp-primary-dark);
        display: inline-flex; align-items: center; justify-content: center;
        margin-bottom: 18px;
        box-shadow: inset 0 0 0 1px rgba(196, 120, 110, 0.12);
      }
      .lp-feature-icon svg { width: 24px; height: 24px; stroke-width: 1.8; }

      /* ── Steps ──────────────────────────────────── */
      .lp-steps {
        background: linear-gradient(135deg, var(--lp-primary) 0%, var(--lp-accent) 100%);
        padding: 88px 0;
        color: #fff;
        position: relative;
        overflow: hidden;
      }
      .lp-steps::before {
        content: '';
        position: absolute; inset: 0;
        background:
          radial-gradient(circle at 10% 10%, rgba(255, 255, 255, 0.14), transparent 30%),
          radial-gradient(circle at 90% 90%, rgba(255, 255, 255, 0.08), transparent 30%);
        pointer-events: none;
      }
      @media (min-width: 1024px) { .lp-steps { padding: 120px 0; } }
      .lp-steps-list {
        list-style: none; padding: 0; margin: 0;
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px;
      }
      @media (min-width: 640px) { .lp-steps-list { grid-template-columns: repeat(2, 1fr); } }
      @media (min-width: 1024px) { .lp-steps-list { grid-template-columns: repeat(4, 1fr); gap: 24px; } }
      .lp-step {
        background: rgba(255, 255, 255, 0.12);
        border: 1px solid rgba(255, 255, 255, 0.22);
        border-radius: var(--lp-radius-lg);
        padding: 24px 22px;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        transition: transform 220ms ease, background 220ms ease;
      }
      .lp-step:hover { transform: translateY(-2px); background: rgba(255, 255, 255, 0.18); }
      .lp-step-num {
        font-family: var(--lp-serif);
        font-weight: 700;
        font-size: 44px;
        line-height: 1;
        letter-spacing: -0.03em;
        color: #fff;
        opacity: 0.55;
        margin-bottom: 14px;
      }
      .lp-step-title {
        font-family: var(--lp-sans);
        font-size: 16px; font-weight: 700;
        color: #fff; margin: 0 0 6px;
      }
      .lp-step-desc {
        font-size: 14px; line-height: 1.5;
        color: rgba(255, 255, 255, 0.85);
        margin: 0;
      }

      /* ── Pricing ────────────────────────────────── */
      .lp-pricing {
        padding: 88px 0;
        background: var(--lp-bg);
      }
      @media (min-width: 1024px) { .lp-pricing { padding: 120px 0; } }
      .lp-price-card {
        max-width: 460px;
        margin: 0 auto;
        background: #fff;
        border: 1px solid var(--lp-border);
        border-radius: var(--lp-radius-xl);
        padding: 36px 32px 32px;
        box-shadow: var(--lp-shadow-lg);
        position: relative;
        overflow: hidden;
      }
      .lp-price-card::before {
        content: '';
        position: absolute; top: 0; left: 0; right: 0; height: 4px;
        background: linear-gradient(90deg, var(--lp-primary), var(--lp-accent), var(--lp-gold));
      }
      .lp-price-ribbon {
        display: inline-block;
        background: var(--lp-primary);
        color: #fff;
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.08em; text-transform: uppercase;
        padding: 5px 12px; border-radius: 999px;
        margin-bottom: 18px;
      }
      .lp-price-plan {
        font-family: var(--lp-serif);
        font-size: 15px; font-weight: 600;
        color: var(--lp-primary-dark);
        letter-spacing: -0.01em;
        margin-bottom: 4px;
      }
      .lp-price-value {
        display: flex; align-items: baseline; gap: 2px;
        margin: 8px 0 6px;
      }
      .lp-price-curr { font-size: 22px; font-weight: 600; color: var(--lp-ink); }
      .lp-price-num {
        font-family: var(--lp-serif);
        font-weight: 700;
        font-size: 76px;
        line-height: 0.95;
        letter-spacing: -0.04em;
        color: var(--lp-ink);
        font-variant-numeric: tabular-nums;
      }
      .lp-price-cents { font-size: 22px; font-weight: 600; color: var(--lp-ink); }
      .lp-price-period { font-size: 16px; color: var(--lp-muted); margin-left: 6px; }
      .lp-price-fine { font-size: 13.5px; color: var(--lp-muted); margin: 0 0 24px; }
      .lp-price-list {
        list-style: none; padding: 0; margin: 0 0 28px;
        display: flex; flex-direction: column; gap: 10px;
      }
      .lp-price-list li {
        display: flex; align-items: flex-start; gap: 10px;
        font-size: 14.5px; color: var(--lp-text); line-height: 1.5;
      }
      .lp-price-list svg {
        flex-shrink: 0; width: 20px; height: 20px;
        padding: 3px;
        background: #E8F5EC; color: #5DAE7B;
        border-radius: 999px;
        stroke-width: 2.5;
      }

      /* ── FAQ ────────────────────────────────────── */
      .lp-faq {
        padding: 88px 0;
        background: var(--lp-bg-elev);
        border-top: 1px solid var(--lp-border);
      }
      @media (min-width: 1024px) { .lp-faq { padding: 120px 0; } }
      .lp-faq-inner { max-width: 780px; }
      .lp-faq-list { display: flex; flex-direction: column; gap: 10px; }
      .lp-faq-item {
        background: var(--lp-bg);
        border: 1px solid var(--lp-border);
        border-radius: var(--lp-radius-md);
        padding: 4px 20px;
        transition: border-color 200ms ease, background 200ms ease;
      }
      .lp-faq-item[open] {
        border-color: var(--lp-primary-light);
        background: #fff;
        box-shadow: var(--lp-shadow-sm);
      }
      .lp-faq-item summary {
        display: flex; align-items: center; justify-content: space-between;
        gap: 14px;
        list-style: none;
        cursor: pointer;
        padding: 18px 0;
        font-size: 16px; font-weight: 600;
        color: var(--lp-ink);
      }
      .lp-faq-item summary::-webkit-details-marker { display: none; }
      .lp-faq-item summary svg {
        flex-shrink: 0; width: 20px; height: 20px;
        color: var(--lp-primary);
        transition: transform 220ms ease;
      }
      .lp-faq-item[open] summary svg { transform: rotate(180deg); }
      .lp-faq-item p {
        margin: 0 0 18px;
        font-size: 15px; line-height: 1.6;
        color: var(--lp-muted);
      }

      /* ── CTA final ──────────────────────────────── */
      .lp-final {
        padding: 88px 0;
        background:
          radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.14), transparent 40%),
          linear-gradient(135deg, var(--lp-primary-dark), var(--lp-primary) 45%, var(--lp-accent) 100%);
        color: #fff;
        text-align: center;
      }
      @media (min-width: 1024px) { .lp-final { padding: 120px 0; } }
      .lp-final-inner { max-width: 720px; margin: 0 auto; }
      .lp-final-sub {
        font-size: 17px;
        color: rgba(255, 255, 255, 0.88);
        line-height: 1.55;
        margin: 12px 0 32px;
      }

      /* ── Mockups ────────────────────────────────── */
      .lp-hero-mock, .lp-showcase-mock { position: relative; perspective: 1400px; }
      .lp-mock {
        background: #fff;
        border-radius: 14px;
        overflow: hidden;
        box-shadow: var(--lp-shadow-lg);
        border: 1px solid var(--lp-border);
        transform: rotate(-1.2deg);
        transition: transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      .lp-hero-mock:hover .lp-mock, .lp-showcase-mock:hover .lp-mock {
        transform: rotate(-0.3deg) translateY(-4px);
      }
      .lp-showcase-mock .lp-mock { transform: rotate(1.2deg); }
      .lp-showcase-mock:hover .lp-mock { transform: rotate(0.3deg) translateY(-4px); }
      .lp-mock-chrome {
        display: flex; align-items: center; gap: 8px;
        background: #F5EEEA;
        border-bottom: 1px solid #E8DED8;
        padding: 12px 14px;
      }
      .lp-mock-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
      .lp-mock-dot-r { background: #EE6A5F; }
      .lp-mock-dot-y { background: #F5BF4F; }
      .lp-mock-dot-g { background: #62C554; }
      .lp-mock-url {
        margin-left: 10px;
        background: #fff;
        padding: 4px 12px;
        border-radius: 6px;
        font-size: 11.5px;
        color: var(--lp-muted);
        border: 1px solid var(--lp-border);
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .lp-mock-body { padding: 18px 18px 22px; }
      .lp-mock-title-row {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 4px;
      }
      .lp-mock-title {
        font-family: var(--lp-serif);
        font-weight: 700;
        font-size: 22px;
        letter-spacing: -0.02em;
        color: var(--lp-ink);
      }
      .lp-mock-cta {
        background: var(--lp-primary);
        color: #fff;
        padding: 6px 14px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 700;
      }
      .lp-mock-date {
        font-size: 12px;
        color: var(--lp-muted);
        margin-bottom: 14px;
      }
      .lp-mock-orders { display: flex; flex-direction: column; gap: 10px; }
      .lp-mock-order {
        display: flex; align-items: center; gap: 12px;
        padding: 12px;
        background: #fff;
        border: 1px solid var(--lp-border);
        border-radius: 12px;
      }
      .lp-mock-avatar {
        width: 36px; height: 36px;
        border-radius: 50%;
        background: var(--lp-primary-light);
        color: var(--lp-primary-dark);
        display: flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 700;
        flex-shrink: 0;
      }
      .lp-mock-order-info { flex: 1; min-width: 0; }
      .lp-mock-order-name {
        font-size: 14px; font-weight: 600; color: var(--lp-ink);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .lp-mock-order-meta { font-size: 12px; color: var(--lp-muted); margin-top: 2px; }
      .lp-mock-pill {
        font-size: 10.5px; font-weight: 700;
        padding: 4px 10px;
        border-radius: 999px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        flex-shrink: 0;
      }

      /* Mockup calendário */
      .lp-cal { margin-top: 4px; }
      .lp-cal-head {
        display: flex; align-items: center; justify-content: center; gap: 12px;
        padding: 4px 0 12px;
      }
      .lp-cal-nav {
        width: 22px; height: 22px;
        display: inline-flex; align-items: center; justify-content: center;
        border-radius: 6px; background: #FDF7F4; color: var(--lp-muted);
        font-size: 14px; font-weight: 600;
        border: 1px solid var(--lp-border);
      }
      .lp-cal-month {
        font-family: var(--lp-serif);
        font-size: 15px; font-weight: 600;
        color: var(--lp-ink);
        letter-spacing: -0.01em;
      }
      .lp-cal-week {
        display: grid; grid-template-columns: repeat(7, 1fr);
        margin-bottom: 6px;
      }
      .lp-cal-wd {
        text-align: center;
        font-size: 10px; font-weight: 700;
        color: var(--lp-soft);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .lp-cal-grid {
        display: grid; grid-template-columns: repeat(7, 1fr);
        gap: 3px;
      }
      .lp-cal-cell {
        position: relative;
        aspect-ratio: 1 / 1;
        display: flex; align-items: center; justify-content: center;
        border-radius: 6px;
        font-size: 12px;
        color: var(--lp-text);
        font-variant-numeric: tabular-nums;
      }
      .lp-cal-empty { visibility: hidden; }
      .lp-cal-cell.has-ev {
        background: #FDF3EF;
        color: var(--lp-primary-dark);
        font-weight: 600;
      }
      .lp-cal-cell.is-hot {
        background: var(--lp-primary-light);
      }
      .lp-cal-cell.is-today {
        background: var(--lp-primary);
        color: #fff;
        font-weight: 700;
        box-shadow: 0 4px 12px rgba(196, 120, 110, 0.35);
      }
      .lp-cal-num { line-height: 1; }
      .lp-cal-badge {
        position: absolute;
        top: 3px; right: 3px;
        min-width: 12px; height: 12px;
        padding: 0 3px;
        border-radius: 6px;
        background: var(--lp-primary);
        color: #fff;
        font-size: 8px; font-weight: 800;
        display: inline-flex; align-items: center; justify-content: center;
        line-height: 1;
        letter-spacing: 0;
      }
      .lp-cal-cell.is-today .lp-cal-badge {
        background: #fff;
        color: var(--lp-primary-dark);
      }
      .lp-cal-legend {
        display: flex; justify-content: center; gap: 14px;
        margin-top: 10px;
        font-size: 10px;
        color: var(--lp-muted);
      }
      .lp-cal-lg-item { display: inline-flex; align-items: center; gap: 5px; }
      .lp-cal-lg-dot { width: 8px; height: 8px; border-radius: 3px; display: inline-block; }
      .lp-cal-lg-today { background: var(--lp-primary); }
      .lp-cal-lg-ev { background: #FDF3EF; border: 1px solid var(--lp-primary-light); }

      /* Mockup Guia */
      .lp-mock-guia-head {
        display: flex; justify-content: space-between; align-items: flex-start;
        margin-bottom: 16px;
      }
      .lp-mock-badge {
        background: linear-gradient(135deg, var(--lp-primary), var(--lp-accent));
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 4px 10px;
        border-radius: 999px;
      }
      .lp-mock-block {
        border: 1px solid var(--lp-border);
        border-radius: 12px;
        padding: 12px 14px;
        margin-bottom: 10px;
        background: #FDFAF8;
      }
      .lp-mock-block-title {
        font-size: 10.5px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--lp-primary-dark);
        margin-bottom: 8px;
      }
      .lp-mock-row {
        display: flex; justify-content: space-between; align-items: center;
        padding: 6px 0;
        font-size: 13px; color: var(--lp-text);
        border-bottom: 1px dashed var(--lp-border);
      }
      .lp-mock-row:last-child { border-bottom: none; }
      .lp-mock-strong { font-weight: 700; color: var(--lp-ink); font-variant-numeric: tabular-nums; }
      .lp-mock-foot {
        margin-top: 8px;
        padding: 10px 14px;
        background: #EAF7EE;
        border-radius: 10px;
        font-size: 12.5px;
        color: #2E7D50;
        display: flex; align-items: center; gap: 8px;
      }
      .lp-mock-foot strong { color: #1E5A38; }
      .lp-mock-check {
        display: inline-flex; align-items: center; justify-content: center;
        width: 18px; height: 18px; border-radius: 50%;
        background: #5DAE7B; color: #fff;
        font-size: 11px; font-weight: 900;
      }

      /* ── Scroll reveal ──────────────────────────── */
      [data-reveal] {
        opacity: 0;
        transform: translateY(24px);
        transition: opacity 620ms cubic-bezier(0.22, 1, 0.36, 1),
                    transform 620ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      [data-reveal].is-visible {
        opacity: 1;
        transform: translateY(0);
      }
      /* Stagger simples nos bento cards */
      .lp-bento [data-reveal]:nth-child(1) { transition-delay: 0ms; }
      .lp-bento [data-reveal]:nth-child(2) { transition-delay: 60ms; }
      .lp-bento [data-reveal]:nth-child(3) { transition-delay: 120ms; }
      .lp-bento [data-reveal]:nth-child(4) { transition-delay: 180ms; }
      .lp-bento [data-reveal]:nth-child(5) { transition-delay: 240ms; }
      .lp-bento [data-reveal]:nth-child(6) { transition-delay: 300ms; }
      .lp-steps-list [data-reveal]:nth-child(1) { transition-delay: 0ms; }
      .lp-steps-list [data-reveal]:nth-child(2) { transition-delay: 80ms; }
      .lp-steps-list [data-reveal]:nth-child(3) { transition-delay: 160ms; }
      .lp-steps-list [data-reveal]:nth-child(4) { transition-delay: 240ms; }

      @media (prefers-reduced-motion: reduce) {
        [data-reveal], [data-reveal].is-visible {
          opacity: 1 !important;
          transform: none !important;
          transition: none !important;
        }
        .lp-mock { transform: none !important; }
        .lp-btn:hover, .lp-card:hover, .lp-step:hover { transform: none !important; }
      }

      /* ── Ajustes finos mobile ───────────────────── */
      @media (max-width: 767px) {
        .lp-hero { padding: 44px 0 56px; }
        .lp-hero-copy { text-align: left; }
        .lp-hero-copy .lp-eyebrow { margin-bottom: 14px; }
        .lp-h1 { margin-bottom: 16px; }
        .lp-sub { margin-bottom: 24px; }
        .lp-cta-row { gap: 10px; margin-bottom: 18px; }
        .lp-cta-row .lp-btn { flex: 1; min-width: 140px; }
        .lp-showcase { padding: 56px 0 64px; }
        .lp-showcase-copy .lp-eyebrow { margin-bottom: 14px; }
        .lp-showcase-list { margin-bottom: 24px; gap: 10px; }
        .lp-features { padding: 56px 0 64px; }
        .lp-steps { padding: 56px 0 64px; }
        .lp-pricing { padding: 56px 0 64px; }
        .lp-faq { padding: 56px 0 64px; }
        .lp-final { padding: 56px 0 72px; }
        .lp-section-head { margin-bottom: 32px; }
        .lp-final-sub { margin-bottom: 24px; }
        .lp-price-card { padding: 28px 22px 26px; }
        .lp-price-num { font-size: 64px; }
        .lp-mock-body { padding: 14px 14px 18px; }
        .lp-mock-title { font-size: 18px; }
        .lp-trust { font-size: 13px; }
      }
      @media (max-width: 480px) {
        .lp-cta-row { flex-direction: column; align-items: stretch; }
        .lp-cta-row .lp-btn { flex: none; width: 100%; min-width: 0; }
        .lp-btn-lg { padding: 14px 18px; font-size: 15px; }
      }

      /* In-app browser warning banner (topo da pagina) */
      .lp-inapp-banner {
        display: flex; align-items: flex-start; gap: 10px;
        background: #FEF3C7; color: #78350F;
        border-bottom: 1px solid #FCD34D;
        padding: 10px 16px; font-size: 13px; line-height: 1.4;
        position: sticky; top: 0; z-index: 100;
      }
      .lp-inapp-banner strong { color: #78350F; font-weight: 700; }
      .lp-inapp-banner svg { flex-shrink: 0; margin-top: 1px; color: #B45309; }
      .lp-inapp-banner span { flex: 1; }
      .lp-inapp-close {
        background: transparent; border: none; color: #78350F;
        cursor: pointer; padding: 2px; display: flex; align-items: center;
        border-radius: 4px; flex-shrink: 0;
      }
      .lp-inapp-close:hover { background: rgba(120, 53, 15, 0.1); }
    `}</style>
  );
}
